// src/pages/CreditNote.jsx
import { colors as themeColors } from '../utils/theme';
import React, { useEffect, useState, useCallback } from "react";
import { Box, Button, Card, CardContent, Grid, Paper, Skeleton, Stack, TextField, Typography, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, IconButton,} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { saveAs } from "file-saver";

const API_BASE = "http://192.168.1.248:3103";

// 🔁 Converts JSON to CSV format
const jsonToCsv = (rows) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (s) => `"${(s ?? "").toString().replace(/"/g, '""')}"`;
    const lines = [ headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")), ];
    return lines.join("\n");
};

// 🔎 Filter Section (Responsive)
const FilterForm = ({ filters, onChange, onSearch, onReset, isMobile }) => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, background: themeColors.neutral.surfaceAlt }}>
        <Typography variant="h6" gutterBottom>Search Credit Notes</Typography>
        <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" flexWrap="wrap" >
            {[
                ["clientName", "Client Name"],
                ["creditNo", "Credit Note No."],
                ["state", "State"],
                ["city", "City"],
                ["installBy", "Installed By"],
            ].map(([name, label]) => (
                <TextField key={name} name={name} label={label} value={filters[name]} onChange={onChange} size="small" fullWidth={isMobile} sx={{ flex: isMobile ? "unset" : 1 }} />
            ))}
            <Button variant="contained" onClick={onSearch}>Search</Button>
            <Button variant="outlined" onClick={onReset}>Reset</Button>
        </Stack>
    </Paper>
);

// 📘 Credit Note Card (Mobile + Desktop Unified Design)
const CreditNoteCards = ({ rows, loading, onView }) => {
    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(3)].map((_, i) => (
                    <Grid item xs={12} key={i}> <Skeleton variant="rectangular" height={140} /> </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            {rows.map((r) => (
                <Grid item xs={12} key={r.creditNoteId}>
                    <Card elevation={3} sx={{ p: 2, borderLeft: `5px solid #007BFF`, borderRadius: 2, transition: "box-shadow 0.3s", "&:hover": { boxShadow: 6 }, }} >
                        <CardContent sx={{ pb: "16px !important" }}>
                            {/* Top Section: Client Info */}
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                                <Box sx={{ bgcolor: "#007BFF", color: themeColors.neutral.surface, p: 1.5, borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 16, mr: 2, }}> {r.companyName?.[0]?.toUpperCase() || "?"} </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontSize="1.1rem" noWrap> {r.companyName} </Typography>
                                    <Typography variant="body2" color="text.secondary"> {r.city}, {r.state} </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: "primary.main" }}> {r.statusName} </Typography>
                            </Box>

                            {/* Info Section */}
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}> <Typography variant="body2"> <strong>CRDNote No:</strong> {r.creditNoteNumber} </Typography> </Grid>
                                <Grid item xs={12} sm={4}> <Typography variant="body2"> <strong>Reference:</strong> {r.referenceNumber || "—"} </Typography> </Grid>
                                <Grid item xs={12} sm={4}> <Typography variant="body2"> <strong>Installed By:</strong> {r.installBy || "—"} </Typography> </Grid>
                            </Grid>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
                                <Button variant="outlined" size="small" onClick={() => onView(r.creditNoteId)}> View </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// 🧾 Main CreditNote Component
function CreditNote() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [filters, setFilters] = useState({
        clientName: "",
        creditNo: "",
        state: "",
        city: "",
        installBy: "",
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [openReturnDialog, setOpenReturnDialog] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    // 📥 Fetch credit note data
    const fetchData = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const { data: resp } = await axios.get(`${API_BASE}/api/get-credit-note`, { params });
            setData(resp || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((f) => ({ ...f, [name]: value }));
    };

    const handleSearch = () => { fetchData(filters); };

    const handleReset = () => {
        setFilters({ clientName: "", creditNo: "", state: "", city: "", installBy: "" });
        fetchData();
    };

    const handleView = (id) => { window.open(`${window.location.origin}/creditNote/${id}`, "_blank"); };

    const handleExport = () => {
        const csv = jsonToCsv(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "credit-notes.csv");
    };

    const handleReturn = (id) => {
        setSelectedInvoiceId(id);
        setOpenReturnDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenReturnDialog(false);
        setSelectedInvoiceId(null);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom align="center">Credit Notes</Typography>

            {/* Filter UI */}
            <FilterForm filters={filters} onChange={handleInputChange} onSearch={handleSearch} onReset={handleReset} isMobile={isMobile} />

            {/* Export Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}> <Button variant="outlined" onClick={handleExport}>Export CSV</Button> </Box>

            {/* Card View */}
            <CreditNoteCards rows={data} loading={loading} onView={handleView} onReturn={handleReturn} />

            {/* Future use: Return Dialog */}
            <Dialog open={openReturnDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs"
                PaperProps={{
                    sx: { borderRadius: 4, p: 2, boxShadow: 8, backgroundColor: theme.palette.background.paper, transition: "all 0.3s ease-in-out", },
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0 }} >
                    <Typography fontWeight={600}>Return Options</Typography>
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3, pb: 3 }}>
                    {/* Optional future form */}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default CreditNote;